import { Badge, Button, Icon, Table, Tooltip } from '@zoom/components';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { OutOfAdherenceDuration } from './OutOfAdherenceDuration';
import { AGENT_DETAILS_CONTAINER_TAG, getAgentDetailsItemTag } from './constants';
import { AgentOverviewWithTeams } from './types';
import { useAuth } from '../../app/AuthorizationProvider';
import { EllipsisContent } from '../../components/EllipsisContent';
import { translateScheduleElementName } from '../../components/MySchedule/transformData';
import { SortedBulkLabels } from '../../components/SortedBulkLabels';
import { BottomPanelHeaderProps, useBottomPanel } from '../../components/layout/BottomPanel';
import { BOTTOM_PANEL_CLOSE_BUTTON } from '../../components/layout/constants';
import { RealTimeAdherenceState, ScheduleElement, Team } from '../../graphql/components';
import { formatNameOfPerson, nameOf } from '../../utils/helpers';

export type RealTimeAdherenceBottomPanelHeaderProps = {
  agentData: AgentOverviewWithTeams;
  timeStamp: number;
  teams: Record<string, Team>;
} & BottomPanelHeaderProps;

export const RealTimeAdherenceBottomPanelHeader: React.FC<RealTimeAdherenceBottomPanelHeaderProps> = ({
  agentData: { firstName, lastName, currentActivity, expectedActivity, inStateSinceTime, state, teamIds },
  onClose,
  timeStamp,
  teams,
}) => {
  const { closeBottomPanel } = useBottomPanel();
  const { t } = useTranslation();
  const { localeObj } = useAuth();

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    closeBottomPanel();
  };

  return (
    <div className="bottom-panel__header position-relative mb-3">
      <Table
        data-test-id={AGENT_DETAILS_CONTAINER_TAG}
        borderless
        className="real-time-adherence__agent-details"
        fixedLayout
        withContainerPadding
      >
        <tbody>
          <tr>
            <EllipsisContent as="td" data-test-id={getAgentDetailsItemTag('name')} className="font-weight-bold">
              {lastName === 'Unknown' && firstName === 'Unknown'
                ? t('person.unknown')
                : formatNameOfPerson(firstName, lastName)}
            </EllipsisContent>
            <td className="col-wrap text-center" data-test-id={getAgentDetailsItemTag('teamIds')}>
              <SortedBulkLabels
                items={teamIds?.map((teamId) => teams[teamId]).filter(Boolean)}
                label={t('team.team', { count: 2 })}
              />
            </td>
            <td
              className="text-center"
              data-test-id={getAgentDetailsItemTag(nameOf<AgentOverviewWithTeams>('currentActivity'))}
            >
              {currentActivity}
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AgentOverviewWithTeams>('expectedActivity'))}
              className="text-wrap text-center"
            >
              {translateScheduleElementName(t, { name: expectedActivity } as ScheduleElement)}
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AgentOverviewWithTeams>('state'))}
              className="text-wrap text-center text-uppercase"
            >
              <Badge kind={state === RealTimeAdherenceState.In ? 'secondary' : 'danger'}>
                {t(`layout.confirm.${state === RealTimeAdherenceState.In ? 'yes' : 'no'}`)}
              </Badge>
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AgentOverviewWithTeams>('inStateSinceTime'))}
              className="text-wrap text-center font-weight-bold"
            >
              {state === RealTimeAdherenceState.In
                ? '--'
                : format(new Date(inStateSinceTime), t('date.formats.full'), { locale: localeObj })}
            </td>
            <td
              data-test-id={getAgentDetailsItemTag('timeOutOfAdherence')}
              className="text-wrap text-center font-weight-bold"
            >
              {state === RealTimeAdherenceState.In ? (
                '--'
              ) : (
                <OutOfAdherenceDuration ooaSinceValue={inStateSinceTime} timeStamp={timeStamp} />
              )}
            </td>
            <td className="actions">
              {/* Intentionally blank to fill the rest of the available space according to Real-Time Adherence table */}
            </td>
          </tr>
        </tbody>
      </Table>
      <div className="real-time-adherence__bottom-panel-corner">
        <Tooltip message={t('layout.action.close')} placement="bottom">
          <Button
            aria-label={t('layout.action.close')}
            className="bottom-panel__close-button"
            data-test-id={BOTTOM_PANEL_CLOSE_BUTTON}
            kind="info"
            onClick={handleClose}
          >
            <Icon color="red" kind="cross" size={12} />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
